import type { ReactNode } from 'react';

export type SafeInline =
  | { type: 'text' | 'emphasis' | 'strong' | 'code'; text: string }
  | { type: 'link'; text: string; href: string };

export type SafeMarkdownBlock =
  | { type: 'heading'; level: 1 | 2 | 3; content: SafeInline[] }
  | { type: 'paragraph' | 'blockquote'; content: SafeInline[] }
  | { type: 'list'; ordered: boolean; items: SafeInline[][] }
  | { type: 'code'; text: string };

export function safeMarkdownUrl(raw: string): string | null {
  const value = raw.trim();
  if (value === '/' || /^(?:\/(?!\/)|#)/.test(value)) return value;
  try {
    const url = new URL(value);
    return ['http:', 'https:', 'mailto:'].includes(url.protocol) ? value : null;
  } catch {
    return null;
  }
}

export function parseSafeInline(value: string): SafeInline[] {
  const pattern = /(`[^`\n]+`|\*\*[^*\n]+\*\*|\*[^*\n]+\*|_[^_\n]+_|\[[^\]\n]+\]\([^\s)]+\))/g;
  const result: SafeInline[] = [];
  let cursor = 0;
  for (const match of value.matchAll(pattern)) {
    const index = match.index ?? 0;
    if (index > cursor) result.push({ type: 'text', text: value.slice(cursor, index) });
    const token = match[0];
    if (token.startsWith('`')) result.push({ type: 'code', text: token.slice(1, -1) });
    else if (token.startsWith('**')) result.push({ type: 'strong', text: token.slice(2, -2) });
    else if (token.startsWith('*') || token.startsWith('_')) {
      result.push({ type: 'emphasis', text: token.slice(1, -1) });
    } else {
      const link = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      const href = link ? safeMarkdownUrl(link[2]!) : null;
      result.push(href
        ? { type: 'link', text: link![1]!, href }
        : { type: 'text', text: link?.[1] ?? token });
    }
    cursor = index + token.length;
  }
  if (cursor < value.length) result.push({ type: 'text', text: value.slice(cursor) });
  return result.length > 0 ? result : [{ type: 'text', text: value }];
}

export function parseSafeMarkdown(markdown: string): SafeMarkdownBlock[] {
  const lines = markdown.replace(/\r\n?/g, '\n').split('\n');
  const blocks: SafeMarkdownBlock[] = [];
  for (let index = 0; index < lines.length;) {
    const line = lines[index]!;
    if (!line.trim()) {
      index += 1;
      continue;
    }
    if (/^```/.test(line)) {
      const code: string[] = [];
      index += 1;
      while (index < lines.length && !/^```/.test(lines[index]!)) code.push(lines[index++]!);
      if (index < lines.length) index += 1;
      blocks.push({ type: 'code', text: code.join('\n') });
      continue;
    }
    const heading = line.match(/^(#{1,3})\s+(.+)$/);
    if (heading) {
      blocks.push({
        type: 'heading',
        level: heading[1]!.length as 1 | 2 | 3,
        content: parseSafeInline(heading[2]!),
      });
      index += 1;
      continue;
    }
    const list = line.match(/^\s*(?:(\d+)\.|[-*+])\s+(.+)$/);
    if (list) {
      const ordered = Boolean(list[1]);
      const items: SafeInline[][] = [];
      while (index < lines.length) {
        const item = lines[index]!.match(/^\s*(?:(\d+)\.|[-*+])\s+(.+)$/);
        if (!item || Boolean(item[1]) !== ordered) break;
        items.push(parseSafeInline(item[2]!));
        index += 1;
      }
      blocks.push({ type: 'list', ordered, items });
      continue;
    }
    if (/^\s*>/.test(line)) {
      const quote: string[] = [];
      while (index < lines.length && /^\s*>/.test(lines[index]!)) {
        quote.push(lines[index++]!.replace(/^\s*>\s?/, ''));
      }
      blocks.push({ type: 'blockquote', content: parseSafeInline(quote.join(' ')) });
      continue;
    }
    const paragraph = [line.trim()];
    index += 1;
    while (index < lines.length && lines[index]!.trim()
      && !/^(?:```|#{1,3}\s|\s*>|\s*(?:(?:\d+)\.|[-*+])\s)/.test(lines[index]!)) {
      paragraph.push(lines[index++]!.trim());
    }
    blocks.push({ type: 'paragraph', content: parseSafeInline(paragraph.join(' ')) });
  }
  return blocks;
}

function Inline({ content }: { content: SafeInline[] }) {
  return content.map((part, index): ReactNode => {
    if (part.type === 'emphasis') return <em key={index}>{part.text}</em>;
    if (part.type === 'strong') return <strong key={index}>{part.text}</strong>;
    if (part.type === 'code') return <code key={index}>{part.text}</code>;
    if (part.type === 'link') {
      const external = /^https?:/.test(part.href);
      return <a key={index} href={part.href} {...(external ? { target: '_blank', rel: 'noreferrer' } : {})}>{part.text}</a>;
    }
    return part.text;
  });
}

export function SafeMarkdown({ markdown }: { markdown: string }) {
  return parseSafeMarkdown(markdown).map((block, index) => {
    if (block.type === 'heading') {
      const Tag = `h${block.level}` as 'h1' | 'h2' | 'h3';
      return <Tag key={index}><Inline content={block.content} /></Tag>;
    }
    if (block.type === 'paragraph') return <p key={index}><Inline content={block.content} /></p>;
    if (block.type === 'blockquote') return <blockquote key={index}><Inline content={block.content} /></blockquote>;
    if (block.type === 'code') return <pre key={index}><code>{block.text}</code></pre>;
    if (block.type !== 'list') return null;
    const Tag = block.ordered ? 'ol' : 'ul';
    return <Tag key={index}>{block.items.map((item: SafeInline[], itemIndex: number) => (
      <li key={itemIndex}><Inline content={item} /></li>
    ))}</Tag>;
  });
}
